/**
 * Whether to tell this person how to install the app.
 *
 * ## Why iOS is the only case here
 *
 * Chromium fires `beforeinstallprompt`, which a page can capture and re-offer later from its
 * own button. Safari fires nothing and has no programmatic install of any kind: the only path
 * is Share → Add to Home Screen, performed by hand. So on Android the right affordance is a
 * button that triggers the real prompt, and on iOS the only possible affordance is a sentence
 * of instructions.
 *
 * Chrome's install-promotion algorithm requires a service worker with a fetch handler, which
 * `sw/sw.ts` now provides — so the event fires in production builds, and `installOffer` below
 * covers both halves. The Settings row (`showsInstallHelp`) remains the iOS-only explanation.
 *
 * ## Why it is a pure function over injected inputs
 *
 * Every branch below is a user-agent or a media query, which is to say: a string and a boolean
 * that no test can set on a real `navigator`. Taking them as arguments is what makes the
 * platform matrix — iPhone, iPad, Android, desktop, already-installed — a table in a test file
 * instead of a thing discovered on somebody's phone.
 */

import { localStore, type Store } from "./localStore.ts";

/** The slice of `navigator` this needs. An interface so tests need no browser. */
export interface Platform {
  readonly userAgent: string;
  /** Safari's own "am I installed" flag. Non-standard, iOS-only, absent everywhere else. */
  readonly standalone?: boolean | undefined;
  /** Present on iPadOS, where the user agent claims to be a Mac. */
  readonly maxTouchPoints?: number | undefined;
}

/**
 * Is this iOS or iPadOS?
 *
 * iPadOS 13 and later report themselves as `Macintosh` with a desktop Safari user agent, on
 * purpose — Apple wanted iPads to get desktop sites. The touch-point count is the standard
 * way back out: a real Mac reports 0 even with a trackpad, an iPad reports 5. Without this an
 * iPad user sees no install help at all, which is a shame because an iPad on a boundary is a
 * genuinely good scoring surface.
 */
export function isIos(platform: Platform): boolean {
  const ua = platform.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && (platform.maxTouchPoints ?? 0) > 1;
}

/**
 * Is the app already running as an installed app rather than in a browser tab?
 *
 * Two signals because neither covers everything: `display-mode: standalone` is the standard
 * and is what Chromium answers, while older iOS answers only `navigator.standalone`. Current
 * iOS answers both. Asking for either is cheap and the cost of being wrong is asymmetric —
 * showing install instructions to someone who has already installed is the one outcome that
 * makes the product look like it is not paying attention.
 */
export function isInstalled(
  platform: Platform,
  matches: (query: string) => boolean,
): boolean {
  if (platform.standalone === true) return true;
  return matches("(display-mode: standalone)");
}

/**
 * Show the "Add to Home Screen" row?
 *
 * On iOS, and not already installed. Everything else gets nothing at all rather than a
 * disabled row or an explanation of why there is no button — a control that exists only to
 * say it does not apply is worse than an absence.
 */
export function showsInstallHelp(
  platform: Platform,
  matches: (query: string) => boolean,
): boolean {
  return isIos(platform) && !isInstalled(platform, matches);
}

/**
 * The same question, answered from the real browser.
 *
 * Guarded because this is called during render and the whole feature is a row in a settings
 * screen: a browser that refuses `matchMedia` should cost that row, never the screen.
 */
export function showsInstallHelpHere(): boolean {
  try {
    return showsInstallHelp(navigator, (query) => window.matchMedia(query).matches);
  } catch {
    return false;
  }
}

// ── The sticky "Add to Home Screen" bar ─────────────────────────────────────

/**
 * What the bar offers, if anything.
 *
 * - `prompt`: Chromium handed over `beforeinstallprompt`, so one tap opens the real install
 *   dialog. Android Chrome, desktop Chrome and Edge.
 * - `ios`: Safari has no programmatic install; the bar says where Share → Add to Home Screen is.
 * - `android-menu`: Android, but no event (yet, or this browser never sends one). The bar
 *   says where the menu item is — the path people were finding on their own, now spelled out.
 *   Replaced by `prompt` the moment the event arrives.
 * - `null`: installed already, or a browser with no install path worth describing.
 *
 * "Installed already" is two signals. Running *as* the installed app is `isInstalled`. A
 * browser tab on a device that has the app is `rememberedInstalled` — Chrome stops sending
 * the prompt then, but the menu and Share hints would otherwise keep asking.
 */
export type InstallOffer = "prompt" | "ios" | "android-menu" | null;

export function installOffer(
  platform: Platform,
  matches: (query: string) => boolean,
  canPrompt: boolean,
  rememberedInstalled = false,
): InstallOffer {
  if (rememberedInstalled || isInstalled(platform, matches)) return null;
  if (canPrompt) return "prompt";
  if (isIos(platform)) return "ios";
  if (/Android/.test(platform.userAgent)) return "android-menu";
  return null;
}

const DISMISSED_KEY = "sportsos.installBar.dismissedAt";
const INSTALLED_KEY = "sportsos.installed";

/**
 * Has this device installed the app? Set by Chrome's `appinstalled` and by any launch of the
 * installed app — on Android the app and Chrome share the origin's storage, so a tab sees it.
 * iOS keeps the home-screen app's storage apart from Safari's, so there a tab never knows;
 * "Cancel" is the answer for that case.
 */
export function rememberedInstalled(store: Store): boolean {
  try {
    return store.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberInstalled(store: Store): void {
  try {
    store.setItem(INSTALLED_KEY, "1");
  } catch {
    // Without storage the hint may show again in a tab. Harmless.
  }
}

/** "Cancel" means a day, not forever: the reason to install has not gone away. */
export const INSTALL_SNOOZE_MS = 24 * 60 * 60 * 1000;

export function installBarSnoozed(store: Store, now: number): boolean {
  try {
    const at = Number(store.getItem(DISMISSED_KEY));
    return Number.isFinite(at) && at > 0 && now - at < INSTALL_SNOOZE_MS;
  } catch {
    return false;
  }
}

export function snoozeInstallBar(store: Store, now: number): void {
  try {
    store.setItem(DISMISSED_KEY, String(now));
  } catch {
    // A browser refusing storage shows the bar again next visit. Harmless.
  }
}

/** Chromium's event. Not in the DOM typings because no other engine has it. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();
const notifyPrompt = (): void => promptListeners.forEach((listener) => listener());

/**
 * Start listening for `beforeinstallprompt`. Call once, before the first render.
 *
 * Before render because Chrome can fire it as soon as the page is eligible, which on a warm
 * load is before React has mounted anything that could listen. `preventDefault` swaps
 * Chrome's own mini-infobar for this app's bar, which can be re-offered later.
 */
export function captureInstallPrompt(target: Window = window): void {
  try {
    target.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      deferredPrompt = event as BeforeInstallPromptEvent;
      notifyPrompt();
    });
    target.addEventListener("appinstalled", () => {
      deferredPrompt = null;
      rememberInstalled(localStore());
      notifyPrompt();
    });
    if (isInstalled(target.navigator, (query) => target.matchMedia(query).matches)) {
      rememberInstalled(localStore());
    }
  } catch {
    // No install bar is better than no app.
  }
}

export const canPromptInstall = (): boolean => deferredPrompt !== null;

/** Subscribe to the prompt arriving or being used up. Returns the unsubscribe. */
export function onInstallPromptChange(listener: () => void): () => void {
  promptListeners.add(listener);
  return () => promptListeners.delete(listener);
}

/** Open Chromium's install dialog. Each captured event can be used exactly once. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferredPrompt;
  if (event === null) return "unavailable";
  deferredPrompt = null;
  notifyPrompt();
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch {
    return "unavailable";
  }
}

/** `installOffer`, answered from the real browser. Guarded for the reason above. */
export function installOfferHere(): InstallOffer {
  try {
    return installOffer(
      navigator,
      (query) => window.matchMedia(query).matches,
      canPromptInstall(),
      rememberedInstalled(localStore()),
    );
  } catch {
    return null;
  }
}
